import type { BooleanOperandContours } from "@/document/operations/booleanGeometry";
import { createIdentityTransform, type SceneNode, type TextNode, type VectorNetworkNode } from "@/document/model/nodes";
import { createDefaultStyle, createSolidPaint, normalizeStrokeStack, styleWithStrokeStack, type NodeStyle, type StrokeStyle } from "@/document/model/styles";
import { textFillInspectorValue } from "@/document/model/text";
import type { VectorEdge, VectorNetwork, VectorStrokeJoin, VectorVertex } from "@/document/model/vectorNetwork";
import { contoursToPathData, pathDataToContours, sceneNodeToContours, type Point } from "@/document/operations/pathGeometry";
import { contoursToVectorNetwork, pathDataToVectorNetwork } from "@/document/operations/pathDataToVectorNetwork";
import { applyAffineToVectorNetwork, frameUsesPatternLayout, patternInstancesForFrame } from "@/document/operations/patternLayout";
import { arcOvalToSvgPath, primitiveToPolygon } from "@/document/operations/primitiveGeometry";
import { getVectorNetworkBounds } from "@/document/operations/vectorGeometry";
import { vectorRegionGeometries } from "@/document/operations/vectorRegions";
import { textNodeToSvgPathData } from "@/export/textVectorize";
import { createId } from "@/lib/ids";
import { rotatePoint, transformRotationOrigin } from "@/lib/math/rotation";
import { ensureCanvasKit } from "@/renderer/text/canvasKitText";

export async function nodeToBooleanOperand(node: SceneNode): Promise<BooleanOperandContours> {
  const contours = await nodeToBooleanContours(node);
  const path = await nodeToBooleanPath(node, contours);
  return {
    nodeId: node.id,
    sourceBounds: { ...node.bounds },
    sourceKey: node.type === "text" ? JSON.stringify(node.text) : undefined,
    path,
    contours,
  };
}

export async function nodesToFlattenVectorNetwork(nodes: SceneNode[]): Promise<VectorNetwork> {
  const networks = (await Promise.all(nodes.map(nodeToFlattenVectorNetworks))).flat();
  return mergeVectorNetworks(networks);
}

export function flattenOutputStyleForNodes(nodes: SceneNode[]): NodeStyle {
  const sourceNode = nodes[0];
  const baseStyle = structuredClone(sourceNode?.style ?? createDefaultStyle());
  if (sourceNode?.type !== "text") {
    return baseStyle;
  }

  const textFill = textFillInspectorValue(
    sourceNode.text,
    { start: 0, end: sourceNode.text.characters.length },
    sourceNode.style.fills.at(-1),
  );
  if (!textFill || textFill === "mixed") {
    return baseStyle;
  }

  return {
    ...baseStyle,
    fills: [structuredClone(textFill)],
  };
}

export async function nodesToOutlinedStrokeVectorNodes(nodes: SceneNode[]): Promise<VectorNetworkNode[]> {
  const outlined = (await Promise.all(nodes.map(nodeToOutlinedStrokeVectorNodes))).flat();
  return outlined;
}

export function createPenVectorNetworkNode(name: string, vertexId: string, point: { x: number; y: number }): VectorNetworkNode {
  return {
    id: createId("node"),
    name,
    type: "vectorNetwork",
    visible: true,
    locked: false,
    transform: createIdentityTransform(),
    bounds: { x: point.x, y: point.y, width: 0, height: 0 },
    style: {
      ...createDefaultStyle("#38bdf8"),
      fills: [],
      strokes: [{
        paint: createSolidPaint("#38bdf8"),
        width: 1,
        align: "center",
      }],
      stroke: {
        paint: createSolidPaint("#38bdf8"),
        width: 1,
        align: "center",
      },
    },
    network: {
      vertices: [{ id: vertexId, x: point.x, y: point.y, handleMode: "mirrorAngleAndLength" }],
      edges: [],
      regions: [],
      fillRule: "nonzero",
    },
  };
}

export function createPencilVectorNetworkNode(name: string, network: VectorNetwork): VectorNetworkNode {
  return {
    id: createId("node"),
    name,
    type: "vectorNetwork",
    visible: true,
    locked: false,
    transform: createIdentityTransform(),
    bounds: getVectorNetworkBounds(network),
    style: {
      ...createDefaultStyle("#38bdf8"),
      fills: [],
      strokes: [{
        paint: createSolidPaint("#38bdf8"),
        width: 1,
        align: "center",
      }],
      stroke: {
        paint: createSolidPaint("#38bdf8"),
        width: 1,
        align: "center",
      },
    },
    network,
  };
}

async function nodeToFlattenVectorNetworks(node: SceneNode): Promise<VectorNetwork[]> {
  if (node.type === "primitive") {
    return [transformNodeVectorNetwork(node, primitiveToFlattenVectorNetwork(node))];
  }

  if (node.type === "text") {
    return [transformNodeVectorNetwork(node, await textNodeToVectorNetwork(node))];
  }

  if (node.type === "vectorNetwork") {
    return [transformNodeVectorNetwork(node, cloneVectorNetworkWithFreshIds(node.network))];
  }

  if (node.type === "shape") {
    return [transformNodeVectorNetwork(node, pathDataToVectorNetwork(node.path))];
  }

  if (node.type === "group" || node.type === "frame" || node.type === "component" || node.type === "componentVariant" || node.type === "instance") {
    if (node.type === "frame" && frameUsesPatternLayout(node)) {
      const instances = patternInstancesForFrame(node);
      const childNetworks = (await Promise.all(node.children.map(async child => {
        const networks = await nodeToFlattenVectorNetworks(child);
        return instances.flatMap(instance => networks.map(network => applyAffineToVectorNetwork(network, instance.transform)));
      }))).flat();
      return [transformNodeVectorNetwork(node, mergeVectorNetworks(childNetworks))];
    }
    const childNetworks = (await Promise.all(node.children.map(nodeToFlattenVectorNetworks))).flat();
    return [transformNodeVectorNetwork(node, mergeVectorNetworks(childNetworks))];
  }

  if (node.type === "boolean") {
    return [transformNodeVectorNetwork(node, node.resolved.path ? pathDataToVectorNetwork(node.resolved.path, { reuseVertices: false }) : contoursToVectorNetwork(node.resolved.contours))];
  }

  return [contoursToVectorNetwork(await nodeToFlattenContours(node))];
}

async function nodeToOutlinedStrokeVectorNodes(node: SceneNode): Promise<VectorNetworkNode[]> {
  const strokes = normalizeStrokeStack(node.style).filter(stroke => stroke.width > 0);
  if (strokes.length === 0) {
    return [];
  }

  const sourcePath = await nodeToStrokeSourcePath(node);
  if (!sourcePath.trim()) {
    return [];
  }

  const closedPath = await nodeToBooleanPath(node, await nodeToBooleanContours(node));
  const closed = closedPath.trim().length > 0;
  const outlined: VectorNetworkNode[] = [];
  const join = nodeStrokeJoin(node);
  for (const [index, stroke] of strokes.entries()) {
    const path = await strokedOutlinePath(sourcePath, closedPath, closed, stroke, join);
    if (!path.trim()) {
      continue;
    }
    const network = pathDataToVectorNetwork(path, { reuseVertices: false });
    if (network.vertices.length === 0 || network.edges.length === 0) {
      continue;
    }
    const bounds = getVectorNetworkBounds(network);
    outlined.push({
      id: createId("node"),
      name: strokes.length > 1 ? `${node.name} Stroke ${index + 1} Outline` : `${node.name} Stroke Outline`,
      type: "vectorNetwork",
      visible: node.visible,
      locked: node.locked,
      transform: createIdentityTransform(),
      bounds,
      style: styleWithStrokeStack({
        ...structuredClone(node.style),
        fills: [structuredClone(stroke.paint)],
        effects: node.style.effects ? structuredClone(node.style.effects) : undefined,
      }, []),
      network,
    });
  }

  return outlined;
}

async function textNodeToVectorNetwork(node: TextNode): Promise<VectorNetwork> {
  const path = await textNodeToSvgPathData(node);
  return path ? pathDataToVectorNetwork(path, { reuseVertices: false }) : emptyVectorNetwork();
}

async function nodeToFlattenContours(node: SceneNode): Promise<Point[][]> {
  if (node.type === "text") {
    return textNodeToContours(node);
  }

  return sceneNodeToContours(node);
}

async function nodeToBooleanContours(node: SceneNode): Promise<Point[][]> {
  if (node.type === "text") {
    const path = await textNodeToSvgPathData(node);
    return path ? pathDataToContours(path, 12) : [];
  }

  return sceneNodeToContours(node);
}

async function nodeToBooleanPath(node: SceneNode, contours: Point[][]): Promise<string> {
  if (contours.length === 0) {
    return "";
  }

  if (node.type === "text") {
    return await textNodeToSvgPathData(node) || contoursToPathData(contours);
  }

  const network = await nodeToBooleanVectorNetwork(node);
  return network ? vectorNetworkToPathData(network) : contoursToPathData(contours);
}

async function nodeToStrokeSourcePath(node: SceneNode): Promise<string> {
  if (node.type === "primitive" && node.primitive.kind === "line") {
    const start = transformNodePoint(node, { x: node.bounds.x, y: node.bounds.y });
    const end = transformNodePoint(node, { x: node.bounds.x + node.bounds.width, y: node.bounds.y + node.bounds.height });
    return `M ${formatPathNumber(start.x)} ${formatPathNumber(start.y)} L ${formatPathNumber(end.x)} ${formatPathNumber(end.y)}`;
  }

  if (node.type === "vectorNetwork") {
    const network = transformNodeVectorNetwork(node, cloneVectorNetworkWithFreshIds(node.network));
    return vectorNetworkRoundedStrokePathData(network) || vectorNetworkStrokePathData(network);
  }

  const contours = node.type === "text" ? await textNodeToContours(node) : sceneNodeToContours(node);
  const path = await nodeToBooleanPath(node, contours);
  if (path.trim()) {
    return path;
  }

  const network = await nodeToBooleanVectorNetwork(node);
  return network ? vectorNetworkStrokePathData(network) : contoursToPathData(contours);
}

async function strokedOutlinePath(
  sourcePathData: string,
  closedPathData: string,
  closed: boolean,
  stroke: StrokeStyle,
  join: VectorStrokeJoin,
): Promise<string> {
  const canvasKit = await ensureCanvasKit();
  if (!canvasKit) {
    throw new Error("CanvasKit is required to outline strokes precisely.");
  }

  const sourcePath = canvasKit.Path.MakeFromSVGString(sourcePathData);
  if (!sourcePath) {
    return "";
  }

  const align = stroke.align ?? "center";
  const strokeWidth = closed && align !== "center" ? stroke.width * 2 : stroke.width;
  const strokedPath = sourcePath.makeStroked({
    width: strokeWidth,
    miter_limit: Math.max(1, stroke.width * 4),
    precision: 1,
    join: canvasKitStrokeJoin(canvasKit, join),
    cap: canvasKit.StrokeCap.Butt,
  });
  sourcePath.delete();
  if (!strokedPath) {
    return "";
  }

  if (!closed || align === "center") {
    try {
      return simplifiedPathSvg(strokedPath);
    } finally {
      strokedPath.delete();
    }
  }

  const closedPath = canvasKit.Path.MakeFromSVGString(closedPathData);
  if (!closedPath) {
    strokedPath.delete();
    return "";
  }

  const op = align === "inside" ? canvasKit.PathOp.Intersect : canvasKit.PathOp.Difference;
  const aligned = canvasKit.Path.MakeFromOp(strokedPath, closedPath, op);
  strokedPath.delete();
  closedPath.delete();
  if (!aligned) {
    return "";
  }

  try {
    return simplifiedPathSvg(aligned);
  } finally {
    aligned.delete();
  }
}

function simplifiedPathSvg(path: NonNullable<ReturnType<NonNullable<Awaited<ReturnType<typeof ensureCanvasKit>>>["Path"]["MakeFromSVGString"]>>): string {
  const simplifiable = path as typeof path & {
    makeSimplified?: () => typeof path | null;
    _makeSimplified?: () => typeof path | null;
  };
  const simplify = simplifiable.makeSimplified ?? simplifiable._makeSimplified;
  const simplified = simplify?.call(path);
  if (!simplified) {
    return path.toSVGString();
  }

  try {
    return simplified.toSVGString();
  } finally {
    simplified.delete();
  }
}

function canvasKitStrokeJoin(canvasKit: NonNullable<Awaited<ReturnType<typeof ensureCanvasKit>>>, join: VectorStrokeJoin) {
  switch (join) {
    case "bevel":
      return canvasKit.StrokeJoin.Bevel;
    case "round":
      return canvasKit.StrokeJoin.Round;
    case "miter":
      return canvasKit.StrokeJoin.Miter;
  }
}

function nodeStrokeJoin(node: SceneNode): VectorStrokeJoin {
  if (node.type !== "vectorNetwork" || node.network.vertices.length === 0) {
    return "miter";
  }

  const joins = node.network.vertices.map(vertex => vertex.strokeJoin ?? "miter");
  const first = joins[0]!;
  return joins.every(join => join === first) ? first : "miter";
}

async function nodeToBooleanVectorNetwork(node: SceneNode): Promise<VectorNetwork | undefined> {
  if (node.type === "primitive") {
    return transformNodeVectorNetwork(node, primitiveToFlattenVectorNetwork(node));
  }

  if (node.type === "vectorNetwork") {
    return transformNodeVectorNetwork(node, cloneVectorNetworkWithFreshIds(node.network));
  }

  if (node.type === "shape") {
    return transformNodeVectorNetwork(node, pathDataToVectorNetwork(node.path));
  }

  if (node.type === "boolean") {
    return transformNodeVectorNetwork(node, node.resolved.path ? pathDataToVectorNetwork(node.resolved.path, { reuseVertices: false }) : contoursToVectorNetwork(node.resolved.contours));
  }

  if (node.type === "group" || node.type === "frame" || node.type === "component" || node.type === "componentVariant" || node.type === "instance") {
    if (node.type === "frame" && frameUsesPatternLayout(node)) {
      const instances = patternInstancesForFrame(node);
      const networks = (await Promise.all(node.children.map(async child => {
        const network = await nodeToBooleanVectorNetwork(child);
        if (!network) {
          return [];
        }
        return instances.map(instance => applyAffineToVectorNetwork(network, instance.transform));
      }))).flat();
      return transformNodeVectorNetwork(node, mergeVectorNetworks(networks));
    }
    const networks = (await Promise.all(node.children.map(nodeToBooleanVectorNetwork))).filter((network): network is VectorNetwork => Boolean(network));
    return transformNodeVectorNetwork(node, mergeVectorNetworks(networks));
  }

  return undefined;
}

function mergeVectorNetworks(networks: VectorNetwork[]): VectorNetwork {
  // Each input may reuse the same vertex/edge ids (e.g. pattern copies after an
  // affine bake). Remap to fresh ids so edges stay attached to their own copy
  // instead of all pointing at the first network's vertices.
  const cloned = networks.map(cloneVectorNetworkWithFreshIds);
  const fillRule = cloned.every(network => network.fillRule === cloned[0]?.fillRule)
    ? cloned[0]?.fillRule ?? "nonzero"
    : "nonzero";

  return {
    vertices: cloned.flatMap(network => network.vertices),
    edges: cloned.flatMap(network => network.edges),
    regions: [],
    fillRule,
  };
}

function cloneVectorNetworkWithFreshIds(network: VectorNetwork): VectorNetwork {
  const vertexIds = new Map(network.vertices.map(vertex => [vertex.id, createId("vertex")]));
  return {
    vertices: network.vertices.map(vertex => ({
      id: vertexIds.get(vertex.id)!,
      x: vertex.x,
      y: vertex.y,
      handleMode: vertex.handleMode,
      strokeJoin: vertex.strokeJoin,
      cornerRadius: vertex.cornerRadius,
    })),
    edges: network.edges.flatMap(edge => {
      const fromVertexId = vertexIds.get(edge.from.vertexId);
      const toVertexId = vertexIds.get(edge.to.vertexId);
      if (!fromVertexId || !toVertexId) {
        return [];
      }

      return [{
        id: createId("edge"),
        from: {
          vertexId: fromVertexId,
          handle: edge.from.handle ? { ...edge.from.handle } : undefined,
        },
        to: {
          vertexId: toVertexId,
          handle: edge.to.handle ? { ...edge.to.handle } : undefined,
        },
        directed: edge.directed,
      }];
    }),
    regions: [],
    fillRule: network.fillRule,
  };
}

function transformNodeVectorNetwork(node: SceneNode, network: VectorNetwork): VectorNetwork {
  const rotation = node.transform.rotation;
  if (!Number.isFinite(rotation) || rotation === 0) {
    return network;
  }

  const origin = transformRotationOrigin(node.bounds, node.transform);
  return {
    ...network,
    vertices: network.vertices.map(vertex => ({ ...vertex, ...rotatePoint(vertex, origin, rotation) })),
    edges: network.edges.map(edge => ({
      ...edge,
      from: {
        ...edge.from,
        handle: edge.from.handle ? rotatePoint(edge.from.handle, origin, rotation) : undefined,
      },
      to: {
        ...edge.to,
        handle: edge.to.handle ? rotatePoint(edge.to.handle, origin, rotation) : undefined,
      },
    })),
  };
}

function transformNodePoint(node: SceneNode, point: Point): Point {
  const rotation = node.transform.rotation;
  if (!Number.isFinite(rotation) || rotation === 0) {
    return point;
  }

  return rotatePoint(point, transformRotationOrigin(node.bounds, node.transform), rotation);
}

function vectorNetworkToPathData(network: VectorNetwork): string {
  const verticesById = new Map(network.vertices.map(vertex => [vertex.id, vertex]));
  return vectorRegionGeometries(network)
    .map(region => {
      const refs = region.edgeRefs;
      if (refs.length === 0) {
        return "";
      }

      const commands: string[] = [];
      for (let index = 0; index < refs.length; index += 1) {
        const ref = refs[index]!;
        const edge = network.edges.find(candidate => candidate.id === ref.edgeId);
        if (!edge) {
          return "";
        }

        const from = verticesById.get(ref.forward ? edge.from.vertexId : edge.to.vertexId);
        const to = verticesById.get(ref.forward ? edge.to.vertexId : edge.from.vertexId);
        if (!from || !to) {
          return "";
        }

        if (index === 0) {
          commands.push(`M ${formatPathNumber(from.x)} ${formatPathNumber(from.y)}`);
        }

        const fromHandle = ref.forward ? edge.from.handle : edge.to.handle;
        const toHandle = ref.forward ? edge.to.handle : edge.from.handle;
        if (fromHandle && toHandle) {
          commands.push(
            `C ${formatPathNumber(fromHandle.x)} ${formatPathNumber(fromHandle.y)} ${formatPathNumber(toHandle.x)} ${formatPathNumber(toHandle.y)} ${formatPathNumber(to.x)} ${formatPathNumber(to.y)}`,
          );
        } else {
          commands.push(`L ${formatPathNumber(to.x)} ${formatPathNumber(to.y)}`);
        }
      }

      commands.push("Z");
      return commands.join(" ");
    })
    .filter(path => path.length > 0)
    .join(" ");
}

function vectorNetworkStrokePathData(network: VectorNetwork): string {
  const regionPath = vectorNetworkToPathData(network);
  return regionPath.trim() ? regionPath : vectorNetworkEdgeChainsToPathData(network);
}

function vectorNetworkRoundedStrokePathData(network: VectorNetwork): string {
  if (!network.vertices.some(vertex => (vertex.cornerRadius ?? 0) > 0) || network.edges.some(edge => edge.from.handle || edge.to.handle)) {
    return "";
  }

  const verticesById = new Map(network.vertices.map(vertex => [vertex.id, vertex]));
  const incident = incidentEdges(network);
  const used = new Set<string>();
  const paths: string[] = [];
  const starts = network.edges
    .filter(edge => (incident.get(edge.from.vertexId)?.length ?? 0) !== 2 || (incident.get(edge.to.vertexId)?.length ?? 0) !== 2)
    .concat(network.edges);

  for (const edge of starts) {
    if (used.has(edge.id)) {
      continue;
    }

    const startVertexId = (incident.get(edge.from.vertexId)?.length ?? 0) === 2 && (incident.get(edge.to.vertexId)?.length ?? 0) !== 2
      ? edge.to.vertexId
      : edge.from.vertexId;
    const chain = edgeChainFrom(network, incident, edge, startVertexId, used);
    const path = roundedEdgeChainToPathData(chain, verticesById);
    if (path) {
      paths.push(path);
    }
  }

  return paths.join(" ");
}

function vectorNetworkEdgeChainsToPathData(network: VectorNetwork): string {
  const verticesById = new Map(network.vertices.map(vertex => [vertex.id, vertex]));
  const incident = incidentEdges(network);
  const used = new Set<string>();
  const paths: string[] = [];
  const starts = network.edges
    .filter(edge => (incident.get(edge.from.vertexId)?.length ?? 0) !== 2 || (incident.get(edge.to.vertexId)?.length ?? 0) !== 2)
    .concat(network.edges);

  for (const edge of starts) {
    if (used.has(edge.id)) {
      continue;
    }

    const startVertexId = (incident.get(edge.from.vertexId)?.length ?? 0) === 2 && (incident.get(edge.to.vertexId)?.length ?? 0) !== 2
      ? edge.to.vertexId
      : edge.from.vertexId;
    const chain = edgeChainFrom(network, incident, edge, startVertexId, used);
    const path = edgeChainToPathData(chain, verticesById);
    if (path) {
      paths.push(path);
    }
  }

  return paths.join(" ");
}

function incidentEdges(network: VectorNetwork): Map<string, VectorEdge[]> {
  const incident = new Map<string, VectorEdge[]>();
  for (const edge of network.edges) {
    incident.set(edge.from.vertexId, [...(incident.get(edge.from.vertexId) ?? []), edge]);
    incident.set(edge.to.vertexId, [...(incident.get(edge.to.vertexId) ?? []), edge]);
  }
  return incident;
}

function edgeChainFrom(
  network: VectorNetwork,
  incident: Map<string, VectorEdge[]>,
  firstEdge: VectorEdge,
  startVertexId: string,
  used: Set<string>,
): Array<{ edge: VectorEdge; forward: boolean }> {
  const chain: Array<{ edge: VectorEdge; forward: boolean }> = [];
  let edge: VectorEdge | undefined = firstEdge;
  let fromVertexId = startVertexId;

  while (edge && !used.has(edge.id)) {
    const forward: boolean = edge.from.vertexId === fromVertexId;
    const toVertexId: string = forward ? edge.to.vertexId : edge.from.vertexId;
    chain.push({ edge, forward });
    used.add(edge.id);

    const candidates: VectorEdge[] = (incident.get(toVertexId) ?? []).filter(candidate => !used.has(candidate.id));
    if (candidates.length !== 1 || ((incident.get(toVertexId)?.length ?? 0) > 2 && chain.length > 0)) {
      break;
    }

    fromVertexId = toVertexId;
    edge = network.edges.find(candidate => candidate.id === candidates[0]!.id);
  }

  return chain;
}

function edgeChainToPathData(chain: Array<{ edge: VectorEdge; forward: boolean }>, verticesById: Map<string, VectorVertex>): string {
  if (chain.length === 0) {
    return "";
  }

  const commands: string[] = [];
  for (const [index, ref] of chain.entries()) {
    const from = verticesById.get(ref.forward ? ref.edge.from.vertexId : ref.edge.to.vertexId);
    const to = verticesById.get(ref.forward ? ref.edge.to.vertexId : ref.edge.from.vertexId);
    if (!from || !to) {
      return "";
    }

    if (index === 0) {
      commands.push(`M ${formatPathNumber(from.x)} ${formatPathNumber(from.y)}`);
    }

    const fromHandle = ref.forward ? ref.edge.from.handle : ref.edge.to.handle;
    const toHandle = ref.forward ? ref.edge.to.handle : ref.edge.from.handle;
    if (fromHandle && toHandle) {
      commands.push(
        `C ${formatPathNumber(fromHandle.x)} ${formatPathNumber(fromHandle.y)} ${formatPathNumber(toHandle.x)} ${formatPathNumber(toHandle.y)} ${formatPathNumber(to.x)} ${formatPathNumber(to.y)}`,
      );
    } else {
      commands.push(`L ${formatPathNumber(to.x)} ${formatPathNumber(to.y)}`);
    }
  }

  return commands.join(" ");
}

function roundedEdgeChainToPathData(chain: Array<{ edge: VectorEdge; forward: boolean }>, verticesById: Map<string, VectorVertex>): string {
  const vertices = edgeChainVertices(chain, verticesById);
  if (vertices.length < 2) {
    return "";
  }

  const closed = vertices.length > 2 && distance(vertices[0]!, vertices.at(-1)!) <= 0.001;
  const points = closed ? vertices.slice(0, -1) : vertices;
  if (points.length < (closed ? 3 : 2)) {
    return "";
  }

  const rounded = points.map((vertex, index) => roundedVertexGeometry(points, index, closed));
  const commands: string[] = [];
  if (closed) {
    commands.push(`M ${formatPathNumber(rounded[0]!.exit.x)} ${formatPathNumber(rounded[0]!.exit.y)}`);
    for (let index = 1; index < points.length; index += 1) {
      const corner = rounded[index]!;
      commands.push(`L ${formatPathNumber(corner.entry.x)} ${formatPathNumber(corner.entry.y)}`);
      if (corner.radius > 0) {
        commands.push(`Q ${formatPathNumber(points[index]!.x)} ${formatPathNumber(points[index]!.y)} ${formatPathNumber(corner.exit.x)} ${formatPathNumber(corner.exit.y)}`);
      }
    }
    const first = rounded[0]!;
    commands.push(`L ${formatPathNumber(first.entry.x)} ${formatPathNumber(first.entry.y)}`);
    if (first.radius > 0) {
      commands.push(`Q ${formatPathNumber(points[0]!.x)} ${formatPathNumber(points[0]!.y)} ${formatPathNumber(first.exit.x)} ${formatPathNumber(first.exit.y)}`);
    }
    commands.push("Z");
    return commands.join(" ");
  }

  commands.push(`M ${formatPathNumber(points[0]!.x)} ${formatPathNumber(points[0]!.y)}`);
  for (let index = 1; index < points.length - 1; index += 1) {
    const corner = rounded[index]!;
    commands.push(`L ${formatPathNumber(corner.entry.x)} ${formatPathNumber(corner.entry.y)}`);
    if (corner.radius > 0) {
      commands.push(`Q ${formatPathNumber(points[index]!.x)} ${formatPathNumber(points[index]!.y)} ${formatPathNumber(corner.exit.x)} ${formatPathNumber(corner.exit.y)}`);
    }
  }
  commands.push(`L ${formatPathNumber(points.at(-1)!.x)} ${formatPathNumber(points.at(-1)!.y)}`);
  return commands.join(" ");
}

function edgeChainVertices(chain: Array<{ edge: VectorEdge; forward: boolean }>, verticesById: Map<string, VectorVertex>): VectorVertex[] {
  const firstRef = chain[0];
  if (!firstRef) {
    return [];
  }

  const first = verticesById.get(firstRef.forward ? firstRef.edge.from.vertexId : firstRef.edge.to.vertexId);
  if (!first) {
    return [];
  }

  const vertices: VectorVertex[] = [first];
  for (const ref of chain) {
    const to = verticesById.get(ref.forward ? ref.edge.to.vertexId : ref.edge.from.vertexId);
    if (!to) {
      return [];
    }
    vertices.push(to);
  }
  return vertices;
}

function roundedVertexGeometry(
  points: VectorVertex[],
  index: number,
  closed: boolean,
): { entry: Point; exit: Point; radius: number } {
  const current = points[index]!;
  const radius = Math.max(0, current.cornerRadius ?? 0);
  if (radius <= 0 || (!closed && (index === 0 || index === points.length - 1))) {
    return { entry: current, exit: current, radius: 0 };
  }

  const previous = points[(index - 1 + points.length) % points.length]!;
  const next = points[(index + 1) % points.length]!;
  const previousDistance = distance(current, previous);
  const nextDistance = distance(current, next);
  const clampedRadius = Math.min(radius, previousDistance / 2, nextDistance / 2);
  if (clampedRadius <= 0.001) {
    return { entry: current, exit: current, radius: 0 };
  }

  return {
    entry: pointToward(current, previous, clampedRadius),
    exit: pointToward(current, next, clampedRadius),
    radius: clampedRadius,
  };
}

function pointToward(from: Point, to: Point, distanceValue: number): Point {
  const length = distance(from, to);
  if (length <= 0.001) {
    return { ...from };
  }
  const t = distanceValue / length;
  return {
    x: from.x + (to.x - from.x) * t,
    y: from.y + (to.y - from.y) * t,
  };
}

function primitiveToFlattenVectorNetwork(node: Extract<SceneNode, { type: "primitive" }>): VectorNetwork {
  if (node.primitive.kind === "oval") {
    const { arcStart = 0, arcSweep = 360, ratio = 0 } = node.primitive;
    if (arcSweep >= 360 && ratio === 0) {
      return ovalPrimitiveToVectorNetwork(node.bounds);
    }
    // Arc / donut oval: convert via SVG path for exact geometry
    const pathData = arcOvalToSvgPath(node.bounds, arcStart, arcSweep, ratio);
    return pathDataToVectorNetwork(pathData, { reuseVertices: false });
  }

  if (node.primitive.kind === "rectangle" && node.primitive.cornerRadii.some(radius => radius > 0)) {
    return roundedRectanglePrimitiveToVectorNetwork(node.bounds, node.primitive.cornerRadii);
  }

  return contoursToVectorNetwork([primitiveToPolygon(node)]);
}

function ovalPrimitiveToVectorNetwork(bounds: { x: number; y: number; width: number; height: number }): VectorNetwork {
  const { x, y, width, height } = bounds;
  const cx = x + width / 2;
  const cy = y + height / 2;
  const rx = width / 2;
  const ry = height / 2;
  const kappa = 0.5522847498307936;
  const top = { id: createId("vertex"), x: cx, y };
  const right = { id: createId("vertex"), x: x + width, y: cy };
  const bottom = { id: createId("vertex"), x: cx, y: y + height };
  const left = { id: createId("vertex"), x, y: cy };

  return {
    vertices: [top, right, bottom, left],
    edges: [
      cubicEdge(top, right, { x: cx + rx * kappa, y }, { x: x + width, y: cy - ry * kappa }),
      cubicEdge(right, bottom, { x: x + width, y: cy + ry * kappa }, { x: cx + rx * kappa, y: y + height }),
      cubicEdge(bottom, left, { x: cx - rx * kappa, y: y + height }, { x, y: cy + ry * kappa }),
      cubicEdge(left, top, { x, y: cy - ry * kappa }, { x: cx - rx * kappa, y }),
    ],
    regions: [],
    fillRule: "evenodd",
  };
}

function roundedRectanglePrimitiveToVectorNetwork(bounds: { x: number; y: number; width: number; height: number }, radii: [number, number, number, number]): VectorNetwork {
  const { x, y, width, height } = bounds;
  const x1 = Math.min(x, x + width);
  const y1 = Math.min(y, y + height);
  const x2 = Math.max(x, x + width);
  const y2 = Math.max(y, y + height);
  const maxRadius = Math.min(Math.abs(width), Math.abs(height)) / 2;
  const [tl, tr, br, bl] = radii.map(radius => Math.max(0, Math.min(maxRadius, radius))) as [number, number, number, number];
  const kappa = 0.5522847498307936;
  const vertices: VectorVertex[] = [];
  const edges: VectorEdge[] = [];
  const vertex = (vx: number, vy: number): VectorVertex => {
    const previous = vertices.at(-1);
    if (previous && distance(previous, { x: vx, y: vy }) <= 0.001) {
      return previous;
    }
    const next = { id: createId("vertex"), x: vx, y: vy };
    vertices.push(next);
    return next;
  };
  const line = (from: VectorVertex, to: VectorVertex): void => {
    if (from.id !== to.id) {
      edges.push(lineEdge(from, to));
    }
  };
  const curve = (from: VectorVertex, to: VectorVertex, fromHandle: Point, toHandle: Point): void => {
    if (from.id !== to.id) {
      edges.push(cubicEdge(from, to, fromHandle, toHandle));
    }
  };

  const topLeftStart = vertex(x1 + tl, y1);
  const topRightStart = vertex(x2 - tr, y1);
  line(topLeftStart, topRightStart);
  const topRightEnd = vertex(x2, y1 + tr);
  tr > 0 ? curve(topRightStart, topRightEnd, { x: x2 - tr + tr * kappa, y: y1 }, { x: x2, y: y1 + tr - tr * kappa }) : line(topRightStart, topRightEnd);
  const bottomRightStart = vertex(x2, y2 - br);
  line(topRightEnd, bottomRightStart);
  const bottomRightEnd = vertex(x2 - br, y2);
  br > 0 ? curve(bottomRightStart, bottomRightEnd, { x: x2, y: y2 - br + br * kappa }, { x: x2 - br + br * kappa, y: y2 }) : line(bottomRightStart, bottomRightEnd);
  const bottomLeftStart = vertex(x1 + bl, y2);
  line(bottomRightEnd, bottomLeftStart);
  const bottomLeftEnd = vertex(x1, y2 - bl);
  bl > 0 ? curve(bottomLeftStart, bottomLeftEnd, { x: x1 + bl - bl * kappa, y: y2 }, { x: x1, y: y2 - bl + bl * kappa }) : line(bottomLeftStart, bottomLeftEnd);
  const topLeftEnd = vertex(x1, y1 + tl);
  line(bottomLeftEnd, topLeftEnd);
  tl > 0 ? curve(topLeftEnd, topLeftStart, { x: x1, y: y1 + tl - tl * kappa }, { x: x1 + tl - tl * kappa, y: y1 }) : line(topLeftEnd, topLeftStart);

  return {
    vertices,
    edges,
    regions: [],
    fillRule: "evenodd",
  };
}

function lineEdge(from: VectorVertex, to: VectorVertex): VectorEdge {
  return {
    id: createId("edge"),
    from: { vertexId: from.id },
    to: { vertexId: to.id },
    directed: false,
  };
}

function cubicEdge(from: VectorVertex, to: VectorVertex, fromHandle: Point, toHandle: Point): VectorEdge {
  return {
    id: createId("edge"),
    from: { vertexId: from.id, handle: { ...fromHandle } },
    to: { vertexId: to.id, handle: { ...toHandle } },
    directed: false,
  };
}

function emptyVectorNetwork(): VectorNetwork {
  return {
    vertices: [],
    edges: [],
    regions: [],
    fillRule: "evenodd",
  };
}

function formatPathNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : Number(value.toFixed(3)).toString();
}

async function textNodeToContours(node: TextNode): Promise<Point[][]> {
  const path = await textNodeToSvgPathData(node);
  if (!path) {
    return [];
  }
  return pathDataToContours(path, 64);
}

function distance(left: Point, right: Point): number {
  return Math.hypot(left.x - right.x, left.y - right.y);
}
